import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  AUDIT_CALLING_REPORT,
  PARIVARTAN_CALLING_REPORT,
} from "../../constants";
import axios from "axios";
import Loader from "../reusable Component/Loader";
import dayjs from "dayjs";
import FilterComponent from "./FilterComponent";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterListIcon from "@mui/icons-material/FilterList";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

function ParivartanIncomingCallReportComponent({}) {
  const empDetails = JSON.parse(localStorage.getItem("userData"));

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc"); // "asc" or "desc"
  const [filters, setFilters] = useState({
    startDate: dayjs().subtract(7, "days").toDate(), // 7 days ago
    endDate: dayjs().toDate(), // Today
    agentName:
      localStorage.getItem("empRole") === "1" ? empDetails.EmployeeName : "",
  });

  const [filterShow, setFilterShow] = useState(false);

  const [callStatistics, setCallStatistics] = useState([]);

  const [metricData, setMetricData] = useState([]);

  const fetchCallStatistics = () => {
    const { startDate, endDate, agentName } = filters;
    setLoading(true);

    // Determine yesterday's date
    const last7days = dayjs().subtract(7, "days").toDate();

    // Format startDate and endDate, defaulting to yesterday if not provided
    const formattedStartDate = startDate
      ? dayjs(startDate).format("YYYY-MM-DD")
      : last7days;
    const formattedEndDate = endDate
      ? dayjs(endDate).format("YYYY-MM-DD")
      : dayjs().toDate();

    // Build query params
    const queryParams = new URLSearchParams();
    queryParams.append("startDate", formattedStartDate);
    queryParams.append("endDate", formattedEndDate);
    if (localStorage.getItem("empRole") === "1") {
      queryParams.append("agentName", empDetails.EmployeeName);
    } else {
      if (agentName) {
        queryParams.append("agentName", agentName); // Join IDs with commas
      }
    }

    axios
      .get(`${PARIVARTAN_CALLING_REPORT}?${queryParams.toString()}`)
      .then((response) => {
        const { data } = response.data;
        setCallStatistics(data.agents);
        setMetricData(data.summary.metrics);
        console.log(response.data.data.summary);

        setLoading(false);
      })
      .catch((error) => {
        console.error(error);
        setLoading(false);
      });
  };

  const location = useLocation();
  const prevFilters = location.state?.prevFilters;
  const [isInitialLoad, setIsInitialLoad] = useState(true); // Flag to track initial load

  // Effect to set filters from prevFilters if available and avoid triggering API call on initial load
  useEffect(() => {
    if (prevFilters && isInitialLoad) {
      // Set filters from prevFilters if available and avoid triggering API on initial load
      setFilters(prevFilters);
      setIsInitialLoad(false); // Mark as no longer initial load
    } else {
      // If no prevFilters, don't trigger the API call until filters are properly set
      setIsInitialLoad(false);
    }
  }, [prevFilters, isInitialLoad]);

  // Effect to trigger API call when filters are updated
  useEffect(() => {
    if (!isInitialLoad && filters.startDate && filters.endDate) {
      // Call API only when filters are valid and set (and not on the initial load)
      fetchCallStatistics();
    }
  }, [filters, isInitialLoad]);

  const handleNav = (agentName) => {
    navigate(`/calling_report/call_details/${agentName}`, {
      state: { prevFilters: filters },
    });
  };

  const handleViewClick = (agentName) => {
    navigate(`/calling_report/call_details/${agentName}`, {
      state: { prevFilters: filters },
    });
  };

  const handleSort = (column) => {
    const isAsc = sortColumn === column && sortDirection === "asc";
    setSortColumn(column);
    setSortDirection(isAsc ? "desc" : "asc");
  };

  const sortedData = [...callStatistics].sort((a, b) => {
    if (!sortColumn) return 0;

    let valueA = a[sortColumn];
    let valueB = b[sortColumn];

    // Special handling for rates (calculate percentages)
    if (sortColumn === "connection_rate") {
      valueA =
        a.total_calls > 0 ? (a.connected_calls / a.total_calls) * 100 : 0;
      valueB =
        b.total_calls > 0 ? (b.connected_calls / b.total_calls) * 100 : 0;
    } else if (sortColumn === "missed_rate") {
      valueA = a.total_calls > 0 ? (a.missed_calls / a.total_calls) * 100 : 0;
      valueB = b.total_calls > 0 ? (b.missed_calls / b.total_calls) * 100 : 0;
    }

    if (valueA < valueB) return sortDirection === "asc" ? -1 : 1;
    if (valueA > valueB) return sortDirection === "asc" ? 1 : -1;
    return 0;
  });

  const handleExport = () => {
    // Define headers for export
    const headers = [
      { key: "Agent Name", value: "agent_name" },
      { key: "Total Calls", value: "total_calls" },
      { key: "Connected Calls", value: "connected_calls" },
      { key: "Missed Calls", value: "missed_calls" },
      { key: "Connection Rate", value: "connection_rate" },
    ];

    // Map data for export
    const exportData = sortedData.map((item) => ({
      "Agent Name": item.agent_name,
      "Total Calls": item.total_calls,
      "Connected Calls": item.connected_calls,
      "Missed Calls": item.missed_calls,
      "Connection Rate": item.connection_rate,
    }));

    // Add total row to exportData
    const totalRow = {
      "Agent Name": "Total",
      "Total Calls": metricData.total_calls || 0,
      "Connected Calls": metricData.connected_calls || 0,
      "Missed Calls": metricData.missed_calls || 0,
      "Connection Rate": metricData.connection_rate || 0,
    };
    exportData.push(totalRow);

    // Create worksheet from data
    const ws = XLSX.utils.json_to_sheet(exportData);

    // Add headers manually if necessary
    XLSX.utils.sheet_add_aoa(ws, [headers.map((header) => header.key)], {
      origin: "A1",
    });

    // Create a workbook and append worksheet
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Table Data");

    // Write workbook to file and download
    XLSX.writeFile(wb, "TableData.xlsx");
  };

  return (
    <>
      {loading && <Loader open={loading} />}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          width: "100%",
        }}
      >
        <Typography
          variant="h4"
          sx={{ fontWeight: 700, paddingLeft: "12px", flexGrow: 1 }}
        >
          Incoming Call Report
        </Typography>

        {/* IconButton aligned to the right */}
        <IconButton
          onClick={() => setFilterShow((prev) => !prev)}
          style={{
            border: "1px solid #fff", // Change border color to white for contrast
            borderRadius: "4px",
            margin: "5px",
            width: "40px", // Increased width for a more comfortable click area
            height: "40px", // Increased height for a more comfortable click area
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#097969",
            color: "#fff",
            transition: "background-color 0.3s, transform 0.2s", // Smooth transition effects
            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)", // Add shadow for depth
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#0B9B73"; // Darker shade on hover
            e.currentTarget.style.transform = "scale(1.05)"; // Slightly scale up on hover
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#097969"; // Original color
            e.currentTarget.style.transform = "scale(1)"; // Reset scale
          }}
        >
          <FilterListIcon />
        </IconButton>

        {/* IconButton aligned to the right */}
        <IconButton
          onClick={handleExport}
          style={{
            border: "1px solid #fff", // Change border color to white for contrast
            borderRadius: "4px",
            margin: "5px",
            width: "40px", // Increased width for a more comfortable click area
            height: "40px", // Increased height for a more comfortable click area
            justifyContent: "center",
            alignItems: "center",
            backgroundColor: "#097969",
            color: "#fff",
            transition: "background-color 0.3s, transform 0.2s", // Smooth transition effects
            boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.2)", // Add shadow for depth
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = "#0B9B73"; // Darker shade on hover
            e.currentTarget.style.transform = "scale(1.05)"; // Slightly scale up on hover
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = "#097969"; // Original color
            e.currentTarget.style.transform = "scale(1)"; // Reset scale
          }}
        >
          <FileDownloadIcon />
        </IconButton>
      </Box>

      {filterShow && (
        <FilterComponent
          filters={filters}
          setFilters={setFilters}
          callStatistics={callStatistics}
          setFilterShow={setFilterShow}
        ></FilterComponent>
      )}

      <Box
        display="flex"
        justifyContent="space-between"
        sx={{
          padding: "16px",
          backgroundColor: "#EEF0F4",
          borderRadius: "8px",
          marginBottom: "16px",
        }}
      >
        {/* Total Calls */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "8px",
            backgroundColor: "#FFFFFF",
            borderRadius: "8px",
            boxShadow: 2,
            width: "20%",
          }}
        >
          <Typography variant="body2" sx={{ color: "#7A7A7A" }}>
            Total Calls
          </Typography>
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", color: "#333333" }}
          >
            {metricData.total_calls || 0}
          </Typography>
        </Box>

        {/* Connected Calls */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "8px",
            backgroundColor: "#FFFFFF",
            borderRadius: "8px",
            boxShadow: 2,
            width: "20%",
          }}
        >
          <Typography variant="body2" sx={{ color: "#7A7A7A" }}>
            Connected Calls
          </Typography>
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", color: "#333333" }}
          >
            {metricData.connected_calls || 0}
          </Typography>
        </Box>

        {/* Missed Calls */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "8px",
            backgroundColor: "#FFFFFF",
            borderRadius: "8px",
            boxShadow: 2,
            width: "20%",
          }}
        >
          <Typography variant="body2" sx={{ color: "#7A7A7A" }}>
            Missed Calls
          </Typography>
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", color: "#333333" }}
          >
            {metricData.missed_calls || 0}
          </Typography>
        </Box>

        {/* Missed Rate */}
        {/* <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "8px",
        backgroundColor: "#FFFFFF",
        borderRadius: "8px",
        boxShadow: 2,
        width: "20%",
      }}
    >
      <Typography variant="body2" sx={{ color: "#7A7A7A" }}>
        Missed Rate
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: "bold", color: "#333333" }}>
        {metricData.missed_rate}
      </Typography>
    </Box> */}

        {/* Connection Rate */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "8px",
            backgroundColor: "#FFFFFF",
            borderRadius: "8px",
            boxShadow: 2,
            width: "20%",
          }}
        >
          <Typography variant="body2" sx={{ color: "#7A7A7A" }}>
            Connection Rate
          </Typography>
          <Typography
            variant="h6"
            sx={{ fontWeight: "bold", color: "#333333" }}
          >
            {metricData.connection_rate || 0}
          </Typography>
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ backgroundColor: "#EEF0F4" }}>
                <TableSortLabel
                  active={sortColumn === "agent_name"}
                  direction={sortDirection}
                  onClick={() => handleSort("agent_name")}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    Agent Name
                  </Typography>
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ backgroundColor: "#EEF0F4" }}>
                <TableSortLabel
                  active={sortColumn === "total_calls"}
                  direction={sortDirection}
                  onClick={() => handleSort("total_calls")}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    Total
                  </Typography>
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ backgroundColor: "#EEF0F4" }}>
                <TableSortLabel
                  active={sortColumn === "connected_calls"}
                  direction={sortDirection}
                  onClick={() => handleSort("connected_calls")}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    Connected
                  </Typography>
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ backgroundColor: "#EEF0F4" }}>
                <TableSortLabel
                  active={sortColumn === "missed_calls"}
                  direction={sortDirection}
                  onClick={() => handleSort("missed_calls")}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    Missed
                  </Typography>
                </TableSortLabel>
              </TableCell>
              <TableCell align="center" sx={{ backgroundColor: "#EEF0F4" }}>
                <TableSortLabel
                  active={sortColumn === "connection_rate"}
                  direction={sortDirection}
                  onClick={() => handleSort("connection_rate")}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    Connection Rate
                  </Typography>
                </TableSortLabel>
              </TableCell>
              {/* <TableCell align="center" sx={{ backgroundColor: "#EEF0F4" }}>
                <TableSortLabel
                  active={sortColumn === "missed_rate"}
                  direction={sortDirection}
                  onClick={() => handleSort("missed_rate")}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                    Missed Rate
                  </Typography>
                </TableSortLabel>
              </TableCell> */}
              <TableCell align="center" sx={{ backgroundColor: "#EEF0F4" }}>
                <Typography variant="subtitle2" sx={{ fontWeight: "bold" }}>
                  Action
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Total Row */}

            <TableRow>
              <TableCell
                sx={{ fontWeight: "bold", backgroundColor: "#EEF0F4" }}
              >
                Total
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: "bold", backgroundColor: "#EEF0F4" }}
              >
                {metricData.total_calls || 0}
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: "bold", backgroundColor: "#EEF0F4" }}
              >
                {metricData.connected_calls || 0}
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: "bold", backgroundColor: "#EEF0F4" }}
              >
                {metricData.missed_calls || 0}
              </TableCell>
              <TableCell
                align="center"
                sx={{ fontWeight: "bold", backgroundColor: "#EEF0F4" }}
              >
                {metricData.connection_rate || 0}
              </TableCell>
              {/* <TableCell
                align="center"
                sx={{ fontWeight: "bold", backgroundColor: "#EEF0F4" }}
              >
                {sortedData.reduce(
                  (sum, item) => sum + item.missed_rate,
                  0
                )}
              </TableCell> */}

              <TableCell
                align="center"
                sx={{ backgroundColor: "#EEF0F4" }}
              ></TableCell>
            </TableRow>

            {sortedData.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>No Data available Yet</TableCell>
              </TableRow>
            ) : (
              sortedData.map((item) => {
                return (
                  <TableRow key={item.agent_name}>
                    <TableCell sx={{ backgroundColor: "#EEF0F4" }}>
                      <Typography
                        variant="body2"
                        sx={{
                          cursor: "pointer",
                          "&:hover": { textDecoration: "underline" },
                        }}
                        onClick={() => handleNav(item.agent_name)}
                      >
                        {item.agent_name}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">{item.total_calls}</TableCell>
                    <TableCell align="center">{item.connected_calls}</TableCell>
                    <TableCell align="center">{item.missed_calls}</TableCell>
                    <TableCell align="center">{item.connection_rate}</TableCell>
                    {/* <TableCell align="center">{item.missed_rate}</TableCell> */}
                    <TableCell align="center">
                      <Tooltip title="View" arrow>
                        <IconButton
                          style={{
                            border: "1px solid",
                            borderRadius: "4px",
                            padding: "2px",
                            width: "24px",
                            height: "24px",
                            backgroundColor: "#00A36C",
                          }}
                          onClick={() => handleViewClick(item.agent_name)}
                        >
                          <VisibilityIcon
                            sx={{ fontSize: "1rem", color: "#fff" }}
                          />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

export default ParivartanIncomingCallReportComponent;
